import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Endocrine subsection adapter
 * Handles endocrine system assessment and menstrual cycle tracking (5 fields)
 */
export class EndocrineAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Endocrine assessment fields
    const endocrineWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL
    )?.at(0)

    const menstrualCycle = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE
    )?.at(0)

    const menstrualDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE
    )?.at(0)

    const endocrineNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.ENDOCRINE_WNL]: endocrineWnl,
      [DischargeFormField.ENDOCRINE_MENSTRUAL_CYCLE]: menstrualCycle,
      [DischargeFormField.ENDOCRINE_MENSTRUAL_DATE]: menstrualDate,
      [DischargeFormField.ENDOCRINE_NOTES]: endocrineNotes,
      [DischargeFormField.ENDOCRINE_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.ENDOCRINE_WNL)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL,
        value: this.getFormDataField(DischargeFormField.ENDOCRINE_WNL)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.ENDOCRINE_MENSTRUAL_CYCLE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE,
        value: this.getFormDataField(DischargeFormField.ENDOCRINE_MENSTRUAL_CYCLE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.ENDOCRINE_MENSTRUAL_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE,
        value: this.getFormDataField(DischargeFormField.ENDOCRINE_MENSTRUAL_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.ENDOCRINE_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES,
        value: this.getFormDataField(DischargeFormField.ENDOCRINE_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.ENDOCRINE_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.ENDOCRINE_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
